import { api } from "./authService";

// IMPORTANT: api.baseURL already contains '/api' (e.g. http://localhost:8082/api)
// NEVER prefix requests with '/api' here — it produces /api/api/... duplication.
// All paths below are relative to the baseURL, so /bonus resolves to /api/bonus.
const BASE = "/bonus";

// ── Config ────────────────────────────────────────────────────────────────────

async function getConfig() {
  try {
    const res = await api.get(`${BASE}/config`);
    return res.data;
  } catch (error) {
    throw error;
  }
}

async function updateConfig(configData) {
  try {
    const res = await api.put(`${BASE}/config`, configData);
    return res.data;
  } catch (error) {
    throw error;
  }
}

// ── Profession Groups ─────────────────────────────────────────────────────────

async function getProfessionGroups() {
  try {
    const res = await api.get(`${BASE}/profession-groups`);
    return res.data;
  } catch (error) {
    throw error;
  }
}

async function getStudentsForGroup(groupKey) {
  try {
    const res = await api.get(`${BASE}/profession-groups/${groupKey}/students`);
    return res.data;
  } catch (error) {
    throw error;
  }
}

async function getMyBonusScope() {
  const res = await api.get(`${BASE}/my-scope`);
  return res.data;
}

async function getVisibleBonusStudents(professionType, semesterOffset = 0) {
  const params = {};
  if (professionType) params.professionType = professionType;
  if (semesterOffset) params.semesterOffset = semesterOffset;
  const res = await api.get(`${BASE}/students`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return res.data;
}

async function getBonusConfig() {
  return getConfig();
}

async function updateBonusConfig(configData) {
  return updateConfig(configData);
}

// ── Student Detail ────────────────────────────────────────────────────────────

async function getStudentDetail(studentId) {
  try {
    const res = await api.get(`${BASE}/students/${studentId}`);
    return res.data;
  } catch (error) {
    throw error;
  }
}

async function getMyBonus() {
  const res = await api.get(`${BASE}/me`);
  return res.data;
}

// Deduplicates concurrent recalculate calls for the same student (prevents React
// Strict Mode double-mount from sending two simultaneous requests to the backend).
const _inFlightRecalc = new Map();

async function recalculate(studentId) {
  if (_inFlightRecalc.has(studentId)) {
    return _inFlightRecalc.get(studentId);
  }

  const promise = api
    .post(`${BASE}/students/${studentId}/recalculate`)
    .then((res) => res.data)
    .finally(() => {
      _inFlightRecalc.delete(studentId);
    });

  _inFlightRecalc.set(studentId, promise);
  return promise;
}

async function saveOverrides(studentId, overrideData) {
  try {
    const res = await api.put(
      `${BASE}/students/${studentId}/overrides`,
      overrideData,
    );
    return res.data;
  } catch (error) {
    throw error;
  }
}

async function pinReview(studentId, { semesterNumber, sourceType, reviewId }) {
  const res = await api.put(`${BASE}/students/${studentId}/review-pin`, {
    semesterNumber,
    sourceType,
    reviewId,
  });
  return res.data;
}

async function finalize(studentId) {
  try {
    const res = await api.post(`${BASE}/students/${studentId}/finalize`);
    return res.data;
  } catch (error) {
    throw error;
  }
}

// ── Formatting helpers ────────────────────────────────────────────────────────

function formatCHF(amount) {
  if (amount == null) return "—";
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatPercent(value) {
  if (value == null) return "—";
  return `${Number(value).toFixed(2)}%`;
}

const bonusService = {
  getMyBonusScope,
  getVisibleBonusStudents,
  getBonusConfig,
  updateBonusConfig,
  getConfig,
  updateConfig,
  getProfessionGroups,
  getStudentsForGroup,
  getStudentDetail,
  getMyBonus,
  recalculate,
  saveOverrides,
  pinReview,
  finalize,
  formatCHF,
  formatPercent,
};

/**
 * "M117 - Datenbanken abfragen" when the subject carries a module code,
 * otherwise just the name. IT and MEDI modules are identified by their code,
 * so it belongs in front of the name wherever grades are listed.
 */
export function formatSubjectLabel(grade) {
  const name = grade?.subjectName || (grade?.subjectId ? `Subject ${grade.subjectId}` : "");
  const code = (grade?.subjectCode || "").trim();
  return code ? `${code} - ${name}` : name;
}

export default bonusService;
